const queries = require('./queries');
const fs = require('fs').promises;
const path = require('path');
const { encode_email, encode_md5 } = require('../../utils/util');

class ServiceResponse {
    constructor(_success, _message, _data = null) {
        this.success = _success;
        this.message = _message;
        this.data = _data;
    }
}

/**
 * @typedef CompanyData
 * @property {number} company_id
 * @property {string} name
 * @property {string} fullname
 * @property {string} email
 * @property {string} phone
 */

/**
 * @typedef UserData
 * @property {number} id
 * @property {string} name
 * @property {string} surname
 * @property {string} email
 */

/**
 * @param {import('pg').Client} client
 * @param {number} company_id 
 * @param {CompanyData} company_data 
 * @param {number} admin_user_id 
 * @param {UserData[]} users 
 */
async function duplicateCompany(client, company_data, users, admin_user_id) {
    try {
        const old_company_id = company_data.company_id;

        // fetch the current company data
        const { rows: companies } = await client.query(queries.GET_COMPANY, [old_company_id]);
        if (companies.length === 0)
            return new ServiceResponse(false, `Couldn't find company with ID ${old_company_id}.`);

        const company = companies[0];

        await client.query('BEGIN');

        const currentDate = new Date();

        // duplicate the company by recreating it
        const create_company_payload = [
            company_data.name, // company name
            company_data.fullname, // company fullname
            company_data.email, // company email
            company.website,
            company.logo,
            company_data.phone, // company phone
            company.adress,
            company.province,
            company.district,
            company.country,
            company.description,
            company.sector_id,
            company.currency_id,
            company.vision,
            company.mission,
            company.values,
            company.total_employee_number,
            company.sust_email,
            company.sust_website,
            company.organization_chart,
            company.facility_number,
            company.promo_code_id,
            company.add_country_id,
            company.add_state_id,
            company.add_province_id,
            admin_user_id, // created by
        ];

        const { rows: created_companies } = await client.query(queries.CREATE_COMPANY, create_company_payload);
        if (created_companies.length === 0)
            return new ServiceResponse(false, `Error while creating the company with the following payload: \n\n ${create_company_payload}`);

        console.log("New company insertion successfully completed.");

        const new_company_id = created_companies[0].company_id;

        const old_uploads_path = path.join(__dirname, '..', '..', 'uploads', `${old_company_id}`);
        const new_uploads_path = path.join(__dirname, '..', '..', 'uploads', `${new_company_id}`);
        await fs.cp(old_uploads_path, new_uploads_path, { recursive: true });

        const exists = async (p) => fs.access(p).then(() => true).catch(() => false);
        const copySuccess = await exists(new_uploads_path);
        if (!copySuccess)
            return new ServiceResponse(false, `Error while copying the uploads directory into "${new_uploads_path}".`);

        const new_logo_path = String(company.logo).replace(String(old_company_id), String(new_company_id));
        const new_organization_chart_path = String(company.organization_chart).replace(String(old_company_id), String(new_company_id));

        const update_logo_and_org_chart = await client.query(queries.UPDATE_LOGO_AND_ORG_CHART, [old_company_id, new_logo_path, new_organization_chart_path]);
        if (update_logo_and_org_chart.rowCount === 0)
            return new ServiceResponse(false, `Error while updating the logo and organization chart paths.`);

        // create facilities
        const { rows: facilities } = await client.query(queries.GET_FACILITIES, [old_company_id]);
        if (facilities.length === 0)
            return new ServiceResponse(false, `Company with ID ${old_company_id} has no facilities, ineligible for duplication!`);

        const facilityMap = new Map();
        for (let idx = 0; idx < facilities.length; idx++) {
            const facility = facilities[idx];
            const facility_payload = [
                new_company_id,
                `${company_data.name} Facility ${facilities.length > 0 ? idx+1 : ''}`,
                facility.description,
                facility.email,
                facility.phone,
                facility.address,
                facility.province,
                facility.district,
                facility.country,
                admin_user_id
            ];

            const { rows: created_facilities } = await client.query(queries.CREATE_FACILITY, facility_payload);
            if (created_facilities.length === 0)
                return new ServiceResponse(false, `Error while creating facility for company with ID ${old_company_id} with the following payload: \n\n ${facility_payload}`);

            facilityMap.set(facility.facility_id, created_facilities[0].facility_id);
        }

        console.log("Facilities duplication successfully completed.");

        // create milestones
        const { rows: milestones } = await client.query(queries.GET_MILESTONES, [old_company_id]);
        for (let idx = 0; idx < milestones.length; idx++) {
            const milestone = milestones[idx];
            const milestone_payload = [
                new_company_id,
                milestone.milestone_year,
                milestone.milestone_text,
                admin_user_id
            ];

            const { rows: created_milestones } = await client.query(queries.CREATE_MILESTONE, milestone_payload);
            if (created_milestones.length === 0)
                return new ServiceResponse(false, `Error while creating milestone for company with ID ${old_company_id} with the following payload: \n\n ${milestone_payload}`);
        }

        console.log("Milestones duplication successfully completed.");

        // create awards
        const { rows: awards } = await client.query(queries.GET_AWARDS, [old_company_id]);
        for (let idx = 0; idx < awards.length; idx++) {
            const award = awards[idx];
            const award_payload = [
                new_company_id,
                award.award_year,
                award.award_text,
                admin_user_id
            ];

            const { rows: created_awards } = await client.query(queries.CREATE_AWARD, award_payload);
            if (created_awards.length === 0)
                return new ServiceResponse(false, `Error while creating award for company with ID ${old_company_id} with the following payload: \n\n ${award_payload}`);
        }

        console.log("Awards duplication successfully completed.");

        // create company_memberships
        const { rows: company_memberships } = await client.query(queries.GET_COMPANY_MEMBERSHIPS, [old_company_id]);
        for (let idx = 0; idx < company_memberships.length; idx++) {
            const company_membership = company_memberships[idx];
            const company_membership_payload = [
                new_company_id,
                company_membership.company_membership_date,
                company_membership.company_membership_name,
                admin_user_id
            ];

            const { rows: created_company_memberships } = await client.query(queries.CREATE_COMPANY_MEMBERSHIP, company_membership_payload);
            if (created_company_memberships.length === 0)
                return new ServiceResponse(false, `Error while creating company_membership for company with ID ${old_company_id} with the following payload: \n\n ${company_membership_payload}`);
        }

        console.log("Company memberships duplication successfully completed.");

        // create company_development_networks
        const { rows: company_development_networks } = await client.query(queries.GET_COMPANY_DEVELOPMENT_NETWORKS, [old_company_id]);
        for (let idx = 0; idx < company_development_networks.length; idx++) {
            const company_development_network = company_development_networks[idx];
            const company_development_network_payload = [
                new_company_id,
                company_development_network.company_development_network_membership_date,
                company_development_network.company_development_network_name,
                admin_user_id
            ];

            const { rows: created_company_development_networks } = await client.query(queries.CREATE_COMPANY_DEVELOPMENT_NETWORK, company_development_network_payload);
            if (created_company_development_networks.length === 0)
                return new ServiceResponse(false, `Error while creating company_development_network for company with ID ${old_company_id} with the following payload: \n\n ${company_development_network_payload}`);
        }

        console.log("Company development networks duplication successfully completed.");

        // create product_groups
        const { rows: product_groups } = await client.query(queries.GET_PRODUCT_GROUPS, [old_company_id]);
        for (let idx = 0; idx < product_groups.length; idx++) {
            const product_group = product_groups[idx];
            const product_group_payload = [
                new_company_id,
                product_group.product_group_name,
                product_group.product_group_name_tr,
                product_group.production_year,
                product_group.production_percent,
                product_group.production_count,
                product_group.production_tonnes,
                product_group.production_revenue_share,
                admin_user_id
            ];

            const { rows: created_product_groups } = await client.query(queries.CREATE_PRODUCT_GROUP, product_group_payload);
            if (created_product_groups.length === 0)
                return new ServiceResponse(false, `Error while creating product_group for company with ID ${old_company_id} with the following payload: \n\n ${product_group_payload}`);
        }

        console.log("Product groups duplication successfully completed.");

        // create report_publishing_status
        const { rows: report_publishing_status } = await client.query(queries.GET_REPORT_PUBLISHING_STATUS, [old_company_id]);
        for (let idx = 0; idx < report_publishing_status.length; idx++) {
            const status = report_publishing_status[idx];
            const report_publishing_status_payload = [
                new_company_id,
                status.reports_published,
                status.report_languages_selected,
            ];

            const { rows: created_report_publishing_status } = await client.query(queries.CREATE_REPORT_PUBLISHING_STATUS, report_publishing_status_payload);
            if (created_report_publishing_status.length === 0)
                return new ServiceResponse(false, `Error while creating report_publishing_status for company with ID ${old_company_id} with the following payload: \n\n ${report_publishing_status_payload}`);
        }

        console.log("Report publishing status duplication successfully completed.");

        // create carbon_options
        const { rows: carbon_options } = await client.query(queries.GET_CARBON_OPTIONS, [old_company_id]);

        if (carbon_options.length > 0) {
            const carbon_options_payload = [
                new_company_id,
                carbon_options[0].base_year_selection,
                carbon_options[0].base_year,
                carbon_options[0].calculation_type,
                carbon_options[0].manual_iso_values,
                carbon_options[0].manual_defra_values,
                carbon_options[0].activity_sector,
                carbon_options[0].target_audience,
                carbon_options[0].reporting_frequency,
                carbon_options[0].head_department_for_reporting,
                carbon_options[0].head_department_for_auditing,
                carbon_options[0].auditing_method,
                carbon_options[0].included_facilities,
                carbon_options[0].entities_out_of_bound,
                carbon_options[0].has_removal_process,
                carbon_options[0].removal_process,
                carbon_options[0].revenue,
                carbon_options[0].calculation_method,
                carbon_options[0].reason_for_method_change,
                carbon_options[0].exclusion_criteria,
                carbon_options[0].designated_confidence_level,
                carbon_options[0].emission_density_calculation,
                carbon_options[0].emission_density_unit,
                carbon_options[0].emission_density_value,
                carbon_options[0].materiality_threshold,
                carbon_options[0].require_internal_audit
            ];
            
            const { rows: created_carbon_options } = await client.query(queries.CREATE_CARBON_OPTIONS, carbon_options_payload);
            if (created_carbon_options.length === 0)
                return new ServiceResponse(false, `Error while creating carbon options for company with ID ${old_company_id} with the following payload: \n\n ${carbon_options_payload}`);
        }

        console.log("Carbon options duplication successfully completed.");

        // create company licence
        const { rows: licences } = await client.query(queries.GET_LICENCE, [old_company_id]);
        if (licences.length === 0)
            return new ServiceResponse(false, `Couldn't find licence for company with ID ${old_company_id}`);

        const licence = licences[0];

        const licence_end_date = new Date(currentDate.getTime());
        licence_end_date.setFullYear(currentDate.getFullYear() + 2);

        const licence_payload = [
            new_company_id,
            licence.plan_id,
            currentDate,
            licence_end_date,
            'ACTIVE',
            licence.optional_module_1_id,
            licence.optional_module_2_id,
            licence.optional_module_3_id,
            licence.optional_module_4_id,
            admin_user_id
        ];

        const { rows: created_licence } = await client.query(queries.CREATE_LICENCE, licence_payload);
        if (created_licence.length === 0) {
            return new ServiceResponse(false, `Error while creating licence with the following payload: \n\n ${licence_payload}`);
        }

        console.log("Company licence duplication successfully completed.");
        
        // create company code
        const { rows: company_codes } = await client.query(queries.GET_COMPANY_CODE, [old_company_id]);
        if (company_codes.length === 0) {
            return new ServiceResponse(false, `Couldn't find company code for company with ID ${old_company_id}`);
        }

        const company_code = company_codes[0];

        if (company_code.regular_company_code === 0 && company_code.supplier_code === 0) {
            return new ServiceResponse(false, `Company with ID ${old_company_id} has a rank that is above regular companies and it cannot be duplicated.`);
        }

        let new_regular_company_code = company_code.regular_company_code;
        let new_supplier_code = company_code.supplier_code;
        if (company_code.supplier_code === 0) {
            const get_biggest_regular_company_code = await client.query(queries.GET_BIGGEST_REGULAR_COMPANY_CODE, [
                company_code.executive_code, 
                company_code.white_label_code
            ]);

            if (get_biggest_regular_company_code.rowCount === 0) {
                new_regular_company_code = 1;
            } else {
                new_regular_company_code = get_biggest_regular_company_code.rows[0].regular_company_code + 1;
            }
        } else {
            const get_biggest_supplier_code = await client.query(queries.GET_BIGGEST_SUPPLIER_COMPANY_CODE, [
                company_code.executive_code, 
                company_code.white_label_code,
                company_code.regular_company_code
            ]);

            if (get_biggest_supplier_code.rowCount === 0) {
                new_supplier_code = 1
            } else {
                new_supplier_code = get_biggest_supplier_code.rows[0].supplier_code + 1;
            }

        }

        const company_code_payload = [
            new_company_id,
            company_code.executive_code,
            company_code.white_label_code,
            new_regular_company_code,
            new_supplier_code,
            admin_user_id
        ];

        const { rows: created_company_code } = await client.query(queries.CREATE_COMPANY_CODE, company_code_payload);
        if (created_company_code.length === 0) {
            return new ServiceResponse(false, `Error while creating company_code with the following payload: \n\n ${company_code_payload}`);
        }

        console.log("Company code duplication successfully completed.");

        // create users

        const userMap = new Map();

        for (let i = 0; i < users.length; i++) {
            const user = users[i];
            const { rows: fetched_users } = await client.query(queries.GET_USER, [user.id]);
            if (fetched_users.length === 0) {
                return new ServiceResponse(false, `Error while fetching original user with ID ${user.id}`);
            }

            const fetched_user = fetched_users[0];

            const user_payload = [
                fetched_user.role_id,
                null,
                fetched_user.userpwd,
                user.name,
                user.surname,
                user.email,
                fetched_user.phone,
                new_company_id,
                fetched_user.is_active,
                encode_email(user.email),
                fetched_user.is_active ? currentDate : fetched_user.activated_on,
                fetched_user.user_licence_id,
                fetched_user.user_department_id,
                fetched_user.plan_id,
                fetched_user.user_language,
                admin_user_id
            ];

            const { rows: created_users } = await client.query(queries.CREATE_USER, user_payload);
            if (created_users.length === 0) {
                return new ServiceResponse(false, `Error while duplicating user of ID ${user.id} with the following payload: \n\n ${user_payload}`);
            }

            userMap.set(user.id, created_users[0]);
        }

        console.log("Users duplication successfully completed.");

        // create process tracking data

        const { rows: get_process_track } = await client.query(queries.GET_PROCESS_TRACK, [old_company_id]);
        if (get_process_track.length === 0) {
            return new ServiceResponse(false, `Error while fetching original process tracks of the company.`);
        }

        const process_track_payload = [
            new_company_id, // company_id
            get_process_track[0].is_step_1_completed, // is_step_1_completed
            get_process_track[0].is_step_2_completed, // is_step_2_completed
            get_process_track[0].is_step_3_completed, // is_step_3_completed
            get_process_track[0].is_step_4_completed, // is_step_4_completed
            get_process_track[0].is_step_5_completed, // is_step_5_completed
            get_process_track[0].is_step_6_completed, // is_step_6_completed
            get_process_track[0].is_step_7_completed, // is_step_7_completed
            get_process_track[0].is_step_8_completed, // is_step_8_completed
            get_process_track[0].is_step_9_completed, // is_step_9_completed
            get_process_track[0].is_step_10_completed, // is_step_10_completed
            get_process_track[0].is_step_11_completed, // is_step_11_completed
            get_process_track[0].is_step_12_completed, // is_step_12_completed
            get_process_track[0].is_step_13_completed, // is_step_13_completed
            get_process_track[0].is_step_14_completed, // is_step_14_completed
            get_process_track[0].description, // description
            get_process_track[0].year, // year
            admin_user_id, // user_id
        ];

        const { rows: create_process_track } = await client.query(queries.CREATE_PROCESS_TRACK, process_track_payload);
        if (create_process_track.length === 0) {
            return new ServiceResponse(false, `Failed to create process tracks for the company.`);
        }

        console.log("Process tracking data duplication successfully completed.");

        // create surveys, participations and results

        // su1 survey
        const su1Map = new Map();
        const { rows: get_su1_surveys } = await client.query(queries.GET_SURVEY('su1'), [old_company_id]);
        for (let i = 0; i < get_su1_surveys.length; i++) {
            const survey = get_su1_surveys[i];
            const token = encode_md5(survey.su1_survey_name + "innoemsurvey" + currentDate);
            const payload = [
                survey.su1_survey_name, survey.su1_s_description,
                new_company_id, token,
                survey.is_send, survey.sending_date,
                survey.is_completed, survey.completed_on,
                survey.reminder_frequency_day, survey.end_date,
                admin_user_id
            ];

            const { rows: create_su1_survey } = await client.query(queries.INSERT_SURVEY('su1'), payload);
            if (create_su1_survey.length === 0) {
                return new ServiceResponse(false, `Error while duplicating SU1 survey with ID "${survey.su1_survey_id}".`);
            }

            const new_survey_id = create_su1_survey[0].su1_survey_id;

            su1Map.set(survey.su1_survey_id, new_survey_id);

            // survey infos
            const { rows: get_survey_infos } = await client.query(queries.GET_SURVEY_INFOS('su1'), [survey.su1_survey_id]);
            if (get_survey_infos.length === 0) {
                console.log(`No survey_info found for SU1 survey with ID "${survey.su1_survey_id}."`);
                continue;
            }

            const survey_infos_payload = [
                new_survey_id, admin_user_id,
                get_survey_infos[0].sent_date, get_survey_infos[0].resolved_date,
                get_survey_infos[0].is_sent, get_survey_infos[0].is_resolved
            ];
            const { rows: create_survey_infos } = await client.query(queries.INSERT_SURVEY_INFOS('su1'), survey_infos_payload);
            if (create_survey_infos.length === 0) {
                return new ServiceResponse(false, `Error creating survey info for survey with ID "${survey.su1_survey_id}."`);
            }

            // survey participation
            const { rows: get_survey_participation } = await client.query(queries.GET_SURVEY_PARTICIPATION('su1'), [survey.su1_survey_id]);
            for (let j = 0; j < get_survey_participation.length; j++) {
                const participation = get_survey_participation[j];

                const new_user = userMap.get(participation.user_id);
                if (!new_user) {
                    console.log(`Old user with ID "${participation.user_id}" was not found as it was likely deleted. Continuing the process regardless.`);
                    continue;
                }

                const survey_participation_payload = [
                    new_survey_id,
                    new_user.user_id,
                    participation.receive_survey_link,
                    participation.survey_link.replace(/token=([^&]+)/, `token=${token}`),
                    admin_user_id
                ];

                const { rows: create_participation } = await client.query(queries.INSERT_SURVEY_PARTICIPATION('su1'), survey_participation_payload);
                if (create_participation.length === 0) {
                    return new ServiceResponse(false, `Error creating survey participation for survey with ID "${survey.su1_survey_id}."`);
                }
            }

            // results
            const { rows: get_global_goals_results } = await client.query(queries.GET_SU1_RESULTS('su1_global_goals_results'), [survey.su1_survey_id]);
            for (let j = 0; j < get_global_goals_results.length; j++) {
                const result = get_global_goals_results[j];
                
                const new_user = userMap.get(result.user_id);
                if (!new_user) {
                    console.log(`Old user with ID "${result.user_id}" was not found as it was likely deleted. Continuing the process regardless.`);
                    continue;
                }

                const global_goals_payload = [
                    new_survey_id,
                    new_company_id,
                    new_user.user_id,
                    result.completed_on,
                    result.su1_global_goals_answer
                ];

                const { rows: create_global_goal_result } = await client.query(queries.INSERT_SURVEY_PARTICIPATION('su1'), [global_goals_payload]);
                if (create_global_goal_result.length === 0) {
                    return new ServiceResponse(false, `Error creating survey global_goal_result for survey with ID "${survey.su1_survey_id}."`);
                }
            }
            
            const { rows: get_sub_goals_results } = await client.query(queries.GET_SU1_RESULTS('su1_sub_goals_results'), [survey.su1_survey_id]);
            for (let j = 0; j < get_sub_goals_results.length; j++) {
                const result = get_sub_goals_results[j];
                
                const new_user = userMap.get(result.user_id);
                if (!new_user) {
                    console.log(`Old user with ID "${result.user_id}" was not found as it was likely deleted. Continuing the process regardless.`);
                    continue;
                }

                const sub_goals_payload = [
                    new_survey_id,
                    new_company_id,
                    new_user.user_id,
                    result.completed_on,
                    result.su1_sub_goals_answer
                ];

                const { rows: create_sub_goal_result } = await client.query(queries.INSERT_SURVEY_PARTICIPATION('su1'), [sub_goals_payload]);
                if (create_sub_goal_result.length === 0) {
                    return new ServiceResponse(false, `Error creating survey sub_goal_result for survey with ID "${survey.su1_survey_id}."`);
                }
            }
            
            const { rows: get_green_deals_results } = await client.query(queries.GET_SU1_RESULTS('su1_green_deals_results'), [survey.su1_survey_id]);
            for (let j = 0; j < get_green_deals_results.length; j++) {
                const result = get_green_deals_results[j];
                
                const new_user = userMap.get(result.user_id);
                if (!new_user) {
                    console.log(`Old user with ID "${result.user_id}" was not found as it was likely deleted. Continuing the process regardless.`);
                    continue;
                }

                const green_deals_payload = [
                    new_survey_id,
                    new_company_id,
                    new_user.user_id,
                    result.completed_on,
                    result.su1_green_deals_answer
                ];

                const { rows: create_green_deals_result } = await client.query(queries.INSERT_SURVEY_PARTICIPATION('su1'), [green_deals_payload]);
                if (create_green_deals_result.length === 0) {
                    return new ServiceResponse(false, `Error creating survey green_deals_result for survey with ID "${survey.su1_survey_id}."`);
                }
            }
            
            const { rows: get_texts_results } = await client.query(queries.GET_SU1_RESULTS('su1_texts_results'), [survey.su1_survey_id]);
            for (let j = 0; j < get_texts_results.length; j++) {
                const result = get_texts_results[j];
                
                const new_user = userMap.get(result.user_id);
                if (!new_user) {
                    console.log(`Old user with ID "${result.user_id}" was not found as it was likely deleted. Continuing the process regardless.`);
                    continue;
                }

                const texts_payload = [
                    new_survey_id,
                    new_company_id,
                    new_user.user_id,
                    result.completed_on,
                    result.su1_texts_answer_1,
                    result.su1_texts_answer_2,
                    result.su1_texts_answer_1_en,
                    result.su1_texts_answer_2_en
                ];

                const { rows: create_texts_result } = await client.query(queries.INSERT_SURVEY_PARTICIPATION('su1'), [texts_payload]);
                if (create_texts_result.length === 0) {
                    return new ServiceResponse(false, `Error creating survey texts_result for survey with ID "${survey.su1_survey_id}."`);
                }
            }
        }

        console.log("SU1 surveys, participations and results duplication successfully completed.");

        // su2 survey
        const su2Map = new Map();
        const { rows: get_su2_surveys } = await client.query(queries.GET_SURVEY('su2'), [old_company_id]);
        for (let i = 0; i < get_su2_surveys.length; i++) {
            const survey = get_su2_surveys[i];
            const token = encode_md5(survey.su2_survey_name + "sustainfinitysurvey" + currentDate);
            const payload = [
                survey.su2_survey_name, survey.su2_s_description,
                new_company_id, token,
                survey.is_send, survey.sending_date,
                survey.is_completed, survey.completed_on,
                survey.reminder_frequency_day, survey.end_date,
                admin_user_id
            ];

            const { rows: create_su2_survey } = await client.query(queries.INSERT_SURVEY('su2'), payload);
            if (create_su2_survey.length === 0) {
                return new ServiceResponse(false, `Error while duplicating SU2 survey with ID "${survey.su2_survey_id}".`);
            }

            const new_survey_id = create_su2_survey[0].su2_survey_id;

            su2Map.set(survey.su2_survey_id, new_survey_id);

            // survey infos
            const { rows: get_survey_infos } = await client.query(queries.GET_SURVEY_INFOS('su2'), [survey.su2_survey_id]);
            if (get_survey_infos.length === 0) {
                console.log(`No survey_info found for SU2 survey with ID "${survey.su2_survey_id}."`);
                continue;
            }

            const survey_infos_payload = [
                new_survey_id, admin_user_id,
                get_survey_infos[0].sent_date, get_survey_infos[0].resolved_date,
                get_survey_infos[0].is_sent, get_survey_infos[0].is_resolved
            ];
            const { rows: create_survey_infos } = await client.query(queries.INSERT_SURVEY_INFOS('su2'), survey_infos_payload);
            if (create_survey_infos.length === 0) {
                return new ServiceResponse(false, `Error creating survey info for survey with ID "${survey.su2_survey_id}."`);
            }

            // survey participation
            const { rows: get_survey_participation } = await client.query(queries.GET_SURVEY_PARTICIPATION('su2'), [survey.su2_survey_id]);
            for (let j = 0; j < get_survey_participation.length; j++) {
                const participation = get_survey_participation[j];

                const new_user = userMap.get(participation.user_id);
                if (!new_user) {
                    console.log(`Old user with ID "${participation.user_id}" was not found as it was likely deleted. Continuing the process regardless.`);
                    continue;
                }

                const survey_participation_payload = [
                    new_survey_id,
                    new_user.user_id,
                    participation.receive_survey_link,
                    participation.survey_link.replace(/token=([^&]+)/, `token=${token}`),
                    admin_user_id
                ];

                const { rows: create_participation } = await client.query(queries.INSERT_SURVEY_PARTICIPATION('su2'), survey_participation_payload);
                if (create_participation.length === 0) {
                    return new ServiceResponse(false, `Error creating survey participation for survey with ID "${survey.su2_survey_id}."`);
                }
            }

            // survey questions
            const questionsMap = new Map();
            const { rows: get_survey_questions } = await client.query(queries.GET_SU2_SURVEY_QUESTIONS, [survey.su2_survey_id]);
            for (let j = 0; j < get_survey_questions.length; j++) {
                const question = get_survey_questions[j];

                const survey_question_payload = [
                    question.sust_element_category_id,
                    question.sust_element_id,
                    new_survey_id,
                    token,
                    admin_user_id
                ];

                const { rows: create_survey_question } = await client.query(queries.INSERT_SU2_SURVEY_QUESTIONS, survey_question_payload);
                if (create_survey_question.length === 0) {
                    return new ServiceResponse(false, `Error creating survey question for survey with ID "${survey.su2_survey_id}."`);
                }

                questionsMap.set(question.su2_cs_id, create_survey_question[0].su2_cs_id);
            }

            // survey results
            const { rows: get_survey_results } = await client.query(queries.GET_SU2_SURVEY_RESULTS, [survey.su2_survey_id]);
            for (let j = 0; j < get_survey_results.length; j++) {
                const result = get_survey_results[j];

                const new_user = userMap.get(result.user_id);
                if (!new_user) {
                    console.log(`Old user with ID "${result.user_id}" was not found as it was likely deleted. Continuing the process regardless.`);
                    continue;
                }

                const result_payload = [
                    new_survey_id,
                    questionsMap.get(result.su2_cs_id),
                    new_company_id,
                    new_user.user_id,
                    result.completed_on,
                    result.su2_cs_answer,
                    result.su2_text_answer,
                    result.su2_text_answer_en
                ];

                const { rows: create_participation } = await client.query(queries.INSERT_SU2_SURVEY_RESULTS, result_payload);
                if (create_participation.length === 0) {
                    return new ServiceResponse(false, `Error creating survey result for survey with ID "${survey.su2_survey_id}."`);
                }
            }
        }

        console.log("SU2 surveys, participations and results duplication successfully completed.");

        // create reports
        const { rows: get_reports } = await client.query(queries.GET_REPORTS, [old_company_id]);
        for (let idx = 0; idx < get_reports.length; idx++) {
            const report = get_reports[idx];
            
            const payload = [
                new_company_id,
                report.my_report_name,
                report.my_report_description,
                report.my_report_link,
                report.report_language,
                report.report_type,
                report.report_status,
                report.report_audit_status,
                report.auditor_comments,
                admin_user_id
            ];

            const { rows: create_report } = await client.query(queries.CREATE_REPORT, payload);
            if (create_report.length === 0) {
                return new ServiceResponse(false, `Error while creating report with ID "${report.my_report_id}".`);
            }
        }

        console.log("Reports duplication successfully completed.");

        // create sections

        const { rows: get_sust_sections } = await client.query(queries.GET_SUST_SECTIONS, [old_company_id]);
        for (let idx = 0; idx < get_sust_sections.length; idx++) {
            const section = get_sust_sections[idx];
            const payload = [
                new_company_id,
                section.sust_report_section_order,
                section.sust_report_section_name,
                section.seed_text,
                section.generated_text,
                section.generated_text_tr,
                section.is_selected,
                admin_user_id,
            ];

            const { rows: create_sust_section } = await client.query(queries.CREATE_SUST_SECTION, payload);
            if (create_sust_section.length === 0) {
                return new ServiceResponse(false, `Error while creating sust_report_section with ID "${section.sust_report_section_id}".`);
            }
        }

        console.log("Sustainability report sections duplication successfully completed.");

        const { rows: get_element_sections } = await client.query(queries.GET_ELEMENT_SECTIONS, [old_company_id]);
        for (let idx = 0; idx < get_element_sections.length; idx++) {
            const section = get_element_sections[idx];
            const payload = [
                new_company_id,
                section.sust_element_id,
                section.seed_text,
                section.generated_text,
                section.generated_text_tr,
                section.generated_header,
                section.generated_header_tr,
                section.is_selected,
                admin_user_id
            ];

            const { rows: create_sust_section } = await client.query(queries.CREATE_ELEMENT_SECTION, payload);
            if (create_sust_section.length === 0) {
                return new ServiceResponse(false, `Error while creating element_section with ID "${section.element_section_id}".`);
            }
        }

        console.log("Element sections duplication successfully completed.");
        
        // create metrics
        const metricMap = new Map();
        const { rows: get_metrics } = await client.query(queries.GET_METRICS, [old_company_id]);
        for (let idx = 0; idx < get_metrics.length; idx++) {
            const metric = get_metrics[idx];
            
            const facility_id = facilityMap.get(metric.facility_id);
            if (!facility_id) {
                console.log(`Metric input creation: Facility with ID "${metric.facility_id}" was likely deleted. Skipping input...`);
                continue;
            }

            const payload = [
                new_company_id,
                facility_id,
                metric.metric_kpi_id,
                metric.sub_metric_kpi_id,
                metric.value,
                metric.year,
                metric.quarter,
                admin_user_id
            ];

            const { rows: create } = await client.query(queries.CREATE_METRIC, payload);
            if (create.length === 0) {
                return new ServiceResponse(false, `Error while creating metric input with ID "${metric.metrics_input_table_id}".`);
            }

            metricMap.set(metric.metrics_input_table_id, create[0].metrics_input_table_id);
        }
        
        const { rows: get_total_metrics } = await client.query(queries.GET_TOTAL_METRICS, [old_company_id]);
        for (let idx = 0; idx < get_total_metrics.length; idx++) {
            const metric = get_total_metrics[idx];

            const payload = [
                new_company_id,
                metric.metric_kpi_id,
                metric.sub_metric_kpi_id,
                metric.value,
                metric.year,
                metric.quarter,
                admin_user_id
            ];

            const { rows: create } = await client.query(queries.CREATE_TOTAL_METRIC, payload);
            if (create.length === 0) {
                return new ServiceResponse(false, `Error while creating total metric input with ID "${metric.total_metrics_input_id}".`);
            }
        }

        console.log("Metrics duplication successfully completed.");
        
        // carbon inputs
        const { rows: get_carbon_emission_group } = await client.query(queries.GET_EMISSION_INPUT_GROUPS, [old_company_id]);
        const emissionGroupMap = new Map();
        for (let idx = 0; idx < get_carbon_emission_group.length; idx++) {
            const group = get_carbon_emission_group[idx];

            const payload = [
                group.name,
                group.description,
                new_company_id,
                admin_user_id
            ];

            const { rows: create } = await client.query(queries.CREATE_EMISSION_INPUT_GROUP, payload);
            if (create.length === 0) {
                return new ServiceResponse(false, `Error while creating emission input group with ID "${group.id}".`);
            }

            emissionGroupMap.set(group.id, create[0].id);
        }

        console.log("Emission groups duplication successfully completed.");

        const carbonInputMap = new Map();
        const { rows: get_carbon_inputs } = await client.query(queries.GET_CARBON_INPUTS, [old_company_id]);
        for (let idx = 0; idx < get_carbon_inputs.length; idx++) {
            const input = get_carbon_inputs[idx];

            const facility_id = facilityMap.get(input.facility_id);
            if (!facility_id) {
                console.log(`Carbon input creation: Facility with ID "${input.facility_id}" was likely deleted. Skipping input...`);
                continue;
            }

            const emission_group_id = emissionGroupMap.get(input.emission_group_id);
            if (!emission_group_id) {
                console.log(`Carbon input creation: Emission Group with ID "${input.emission_group_id}" was likely deleted. Skipping input...`);
                continue;
            }

            const payload = [
                input.sub_category_number,
                input.name,
                input.description,
                input.emission_factor_name,
                input.mobility_type_id,
                input.sector_id,
                input.combustion_factor_id,
                input.defra_factor_id,
                input.refrigerant_factor_id,
                input.gwp_factor_id,
                input.wtt_factor_id,
                input.maintenance_load,
                input.maintenance_date,
                input.country_id,
                input.power_line_id,
                input.trip_id,
                input.trip_type,
                input.hotel_stay_factor_id,
                input.electric_factor_id,
                input.defra_vehicle_factor_id,
                input.epa_factor_id,
                input.unit,
                input.amount,
                input.sustainable_electricity_percentage,
                input.density,
                input.oxidation_factor,
                input.year,
                input.quarter,
                new_company_id,
                facility_id,
                emission_group_id,
                admin_user_id
            ];

            const { rows: create } = await client.query(queries.CREATE_EMISSION_INPUT, payload);
            if (create.length === 0) {
                return new ServiceResponse(false, `Error while creating carbon input with ID "${input.id}".`);
            }

            carbonInputMap.set(input.id, create[0].id);

            const { rows: factors } = await client.query(queries.GET_EMISSION_FACTOR, [input.id]);
            if (factors.length === 0) {
                return new ServiceResponse(false, `Error while fetching emission factors for carbon input with ID "${input.id}".`);
            }

            const factor_payload = [
                create[0].id,
                factors[0].ncv_default,
                factors[0].co2_default,
                factors[0].ch4_default,
                factors[0].n2o_default,
                factors[0].emission_factor_operation_lower,
                factors[0].emission_factor_initial_lower,
                factors[0].kgco2e,
                factors[0].kgco2e_wtt,
                factors[0].kgco2e_of_co2_per_unit,
                factors[0].kgco2e_of_ch4_per_unit,
                factors[0].kgco2e_of_n2o_per_unit,
                factors[0].production,
                factors[0].td_loss_percentage,
                factors[0].hotel_stay_factor_value,
                factors[0].epa_margin,
                factors[0].epa_without_margin,
                factors[0].epa_with_margin,
                factors[0].custom_data,
                factors[0].composition
            ];

            const { rows: create_factor } = await client.query(queries.CREATE_EMISSION_FACTOR, factor_payload);
            if (create_factor.length === 0) {
                return new ServiceResponse(false, `Error while creating emission factor for carbon input with ID "${input.id}".`);
            }

            const { rows: outputs } = await client.query(queries.GET_EMISSION_OUTPUT, [input.id]);
            if (outputs.length === 0) {
                return new ServiceResponse(false, `Error while fetching emission outputs for carbon input with ID "${input.id}".`);
            }
            
            const output_payload = [
                create[0].id,
                outputs[0].co2,
                outputs[0].ch4,
                outputs[0].n2o,
                outputs[0].co2e,
                outputs[0].unsustainable_co2e,
                outputs[0].co2e_ch4,
                outputs[0].co2e_n2o,
                outputs[0].leakage_co2e,
                outputs[0].stray_co2e,
                outputs[0].wtt_co2e,
                outputs[0].margin_co2e,
                outputs[0].without_margin_co2e,
                outputs[0].custom_data,
                outputs[0].hfc,
                outputs[0].pfc,
                outputs[0].sf6,
                outputs[0].nf3
            ];

            const { rows: create_output } = await client.query(queries.CREATE_EMISSION_OUTPUT, output_payload);
            if (create_output.length === 0) {
                return new ServiceResponse(false, `Error while creating emission output for carbon input with ID "${input.id}".`);
            }

            const { rows: ghost_outputs } = await client.query(queries.GET_EMISSION_GHOST_OUTPUT, [input.id]);
            if (ghost_outputs.length === 0) {
                return new ServiceResponse(false, `Error while fetching emission ghost outputs for carbon input with ID "${input.id}".`);
            }

            const ghost_output_payload = [
                create[0].id,
                ghost_outputs[0].category_number,
                ghost_outputs[0].sub_category_number,
                ghost_outputs[0].co2e,
                ghost_outputs[0].unsustainable_co2e
            ];

            const { rows: create_ghost_output } = await client.query(queries.CREATE_EMISSION_GHOST_OUTPUT, ghost_output_payload);
            if (create_ghost_output.length === 0) {
                return new ServiceResponse(false, `Error while creating emission ghost output for carbon input with ID "${input.id}".`);
            }
        }

        console.log("Carbon inputs duplication successfully completed.");
        
        // create documents
        const { rows: get_documents } = await client.query(queries.GET_DOCUMENTS, [old_company_id]);
        for (let idx = 0; idx < get_documents.length; idx++) {
            const document = get_documents[idx];

            const facility_id = facilityMap.get(document.facility_id);
            if (!facility_id) {
                console.log(`Document creation: Facility with ID "${document.facility_id}" was likely deleted. Skipping document...`);
                continue;
            }
            
            const payload = [
                document.hash,
                document.name,
                document.path,
                document.old_path,
                new_company_id,
                facility_id,
                admin_user_id
            ];

            const { rows: create_document } = await client.query(queries.CREATE_DOCUMENT, payload);
            if (create_document.length === 0) {
                return new ServiceResponse(false, `Error while creating document with ID "${document.document_id}".`);
            }

            const { rows: get_document_relations } = await client.query(queries.GET_DOCUMENT_RELATIONS, [document.document_id]);
            if (get_document_relations.length === 0) {
                console.log(`No document relations found for document with ID "${document.document_id}". Continuing without creating document relations.`);
                continue;
            }

            const new_carbon_category_1_inputs = [];
            const new_carbon_category_2_inputs = [];
            const new_carbon_category_3_inputs = [];
            const new_carbon_category_4_inputs = [];
            const new_carbon_category_5_inputs = [];
            const new_carbon_category_6_inputs = [];
            const new_metric_inputs = [];
            const new_sub_metric_inputs = [];

            get_document_relations[0].carbon_category_1_inputs.forEach(id => {
                const new_id = carbonInputMap.get(id);
                new_carbon_category_1_inputs.push(new_id);
            });

            get_document_relations[0].carbon_category_2_inputs.forEach(id => {
                const new_id = carbonInputMap.get(id);
                new_carbon_category_2_inputs.push(new_id);
            });

            get_document_relations[0].carbon_category_3_inputs.forEach(id => {
                const new_id = carbonInputMap.get(id);
                new_carbon_category_3_inputs.push(new_id);
            });

            get_document_relations[0].carbon_category_4_inputs.forEach(id => {
                const new_id = carbonInputMap.get(id);
                new_carbon_category_4_inputs.push(new_id);
            });

            get_document_relations[0].carbon_category_5_inputs.forEach(id => {
                const new_id = carbonInputMap.get(id);
                new_carbon_category_5_inputs.push(new_id);
            });

            get_document_relations[0].carbon_category_6_inputs.forEach(id => {
                const new_id = carbonInputMap.get(id);
                new_carbon_category_6_inputs.push(new_id);
            });

            get_document_relations[0].metric_inputs.forEach(id => {
                const new_id = metricMap.get(id);
                new_metric_inputs.push(new_id);
            });

            get_document_relations[0].sub_metric_inputs.forEach(id => {
                const new_id = metricMap.get(id);
                new_sub_metric_inputs.push(new_id);
            });
        }

        console.log("Documents duplication successfully completed.");

        await client.query('COMMIT');
        return new ServiceResponse(true, "Company duplicated successfully. You can login on one of the company's users with the same password as before to see the duplicated data.");
    } catch (error) {
        return new ServiceResponse(false, error.message);
    }
}

/**
 * Gets the details of a company, including its users, facility information and vital identification information.
 * @param {import('pg').Client} client
 * @param {number} company_id 
 */
async function getCompanyDetails(client, company_id) {
    try {
        const { rows: company_details } = await client.query(queries.GET_COMPANY_DETAILS, [company_id]);
        if (company_details.length === 0) {
            return new ServiceResponse(false, "Company not found.");
        }

        return new ServiceResponse(true, "Company details fetched successfully.", company_details[0]);
    } catch (error) {
        return new ServiceResponse(false, error.message);
    }
}

module.exports = {
    duplicateCompany,
    getCompanyDetails,
};